import { TELEGRAM_URL, WHATSAPP_URL } from "@/lib/contact";
import { useT } from "@/i18n";

/** WhatsApp și Telegram, ca modalități de contact.
 *
 *  Glifele sunt cele oficiale, desenate literă cu literă din conturul de marcă
 *  — nu redesenate de mână. Prima încercare a fost un contur de 1px făcut de
 *  mine, ca să stea în limbajul de linie al site-ului: a ieșit un blob strâmb
 *  care nu se citea ca WhatsApp. O marcă e recunoscută după siluetă, iar
 *  silueta nu se aproximează.
 *
 *  Adaptarea la site e cromatică, nu geometrică: `fill:currentColor`, deci
 *  moștenesc cerneala contextului — stins în bară, mai deschis la hover.
 *  Verdele și albastrul original ar fi singurele pete colorate de pe un site
 *  monocrom și s-ar citi ca doi stickeri lipiți.
 *
 *  Linkurile apar doar dacă adresele sunt configurate la build. Un canal de
 *  contact care duce nicăieri e mai rău decât un canal care lipsește. */

function MarkWhatsApp() {
  return (
    <svg className="chmark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

function MarkTelegram() {
  return (
    <svg className="chmark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {/* Avionul singur, nu cel din disc plin: alături de bula WhatsApp, un
          disc umplut e o pată la 16px, iar cele două n-ar mai fi din aceeași
          familie de greutate. */}
      <path d="M23.91 3.79 20.3 20.84c-.25 1.21-.98 1.5-2 .94l-5.5-4.07-2.66 2.57c-.3.3-.55.56-1.1.56-.72 0-.6-.27-.84-.95L6.3 13.7l-5.45-1.7c-1.18-.35-1.19-1.16.26-1.75l21.26-8.2c.97-.44 1.9.24 1.53 1.73z" />
    </svg>
  );
}

const CHANNELS = [
  { key: "whatsapp", url: WHATSAPP_URL, label: "WhatsApp", Mark: MarkWhatsApp },
  { key: "telegram", url: TELEGRAM_URL, label: "Telegram", Mark: MarkTelegram },
] as const;

/** Există măcar un canal configurat? Pagina de contact are nevoie să știe:
 *  altfel randa o casetă cu titlu și explicație, dar fără niciun link. */
export const HAS_CHANNELS = CHANNELS.some((c) => c.url);

/** `bare` = doar mărcile, pentru bara de navigație, unde nu e loc de cuvinte. */
export default function Channels({ bare = false }: { bare?: boolean }) {
  const t = useT();
  const live = CHANNELS.filter((c) => c.url);
  if (!live.length) return null;

  return (
    <>
      {live.map(({ key, url, label, Mark }) => (
        <a
          key={key}
          className={bare ? "chan chan-bare" : "chan"}
          href={url as string}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={bare ? `${label} — ${t.contactPage.chatHead}` : undefined}
          title={bare ? label : undefined}
        >
          <Mark />
          {bare ? null : <span>{label}</span>}
        </a>
      ))}
    </>
  );
}
